import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Heart, BedDouble } from "lucide-react";
import { Property } from "@/pages/Properties";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useFavoritesContext } from "@/contexts/FavoritesContext";
import { useMemo, useState } from "react";
import AuthRequiredDialog from "@/components/AuthRequiredDialog";
import { capture } from "@/lib/analytics";

interface PropertyCardProps {
  property: Property;
}

const PropertyCard = ({ property }: PropertyCardProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavoritesContext();
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const formatPrice = (price: number | null) => {
    if (!price) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Calculate price deviation from AI valuation
  const priceAnalysis = useMemo(() => {
    const price = property.price;
    const aiValuation = property.ai_valuation;

    if (!price || !aiValuation) {
      return null;
    }

    // Calculate percentage difference: positive = overpriced, negative = good deal
    const percentDiff = ((price - aiValuation) / aiValuation) * 100;
    
    // Slider position: 0 = leftmost (best deal), 50 = center (fair), 100 = rightmost (overpriced)
    // Clamp between 0 and 100
    const sliderPosition = Math.max(0, Math.min(100, 50 + percentDiff));

    let label: string;
    let labelColor: string;
    
    if (percentDiff <= -50) {
      label = t.propertyCard.significantlyBelow;
      labelColor = "text-green-600";
    } else if (percentDiff <= -5) {
      label = `${Math.abs(Math.round(percentDiff))}% ${t.propertyCard.belowMarket}`;
      labelColor = "text-green-600";
    } else if (percentDiff >= 70) {
      label = t.propertyCard.significantlyAbove;
      labelColor = "text-red-500";
    } else if (percentDiff >= 5) {
      label = `${Math.round(percentDiff)}% ${t.propertyCard.aboveMarket}`;
      labelColor = "text-red-500";
    } else {
      // Fair price
      label = t.propertyCard.fairPrice;
      labelColor = "text-muted-foreground";
    }

    return {
      percentDiff,
      sliderPosition,
      label,
      labelColor,
      aiEstimate: aiValuation,
    };
  }, [property.price, property.ai_valuation, t.propertyCard]);

  const handleCardClick = () => {
    capture('open_listing', { property_id: property.id, city: property.city, price: property.price });
    window.open(`/property/${property.id}`, '_blank');
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    if (property.id) {
      await toggleFavorite(property.id);
    }
  };

  const saved = property.id ? isFavorite(property.id) : false;

  return (
    <>
      <Card 
        className="group overflow-hidden card-shadow hover:card-shadow-hover transition-all duration-300 cursor-pointer h-full flex flex-col bg-card"
        onClick={handleCardClick}
      >
        {/* Image Section */}
        <div className="relative overflow-hidden flex-shrink-0 p-3 pb-0">
          <div className="relative rounded-xl overflow-hidden">
            <img
              src={property.photo_preview || "/placeholder.svg"}
              alt={property.type || "Property"}
              className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
            />
            {/* Property Type Badge */}
            {property.type && (
              <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
                <span className="text-xs font-medium text-foreground">
                  {property.type}
                </span>
              </div>
            )}
            {/* Favorite Button */}
            <button 
              className="absolute top-3 right-3 bg-card/90 backdrop-blur-sm p-2 rounded-full hover:bg-card transition-colors"
              onClick={handleFavoriteClick}
              aria-label={saved ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart className={`h-5 w-5 transition-colors ${saved ? 'fill-red-500 text-red-500' : 'text-muted-foreground hover:text-red-500'}`} />
            </button>
          </div>
        </div>

        <CardContent className="p-4 flex flex-col flex-1">
          {/* Price Row */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-2xl font-bold text-foreground">
              {formatPrice(property.price)}
            </span>
            {priceAnalysis && (
              <Badge variant="secondary" className="text-xs font-medium">
                {t.propertyCard.aiEstimate}: €{Math.round(priceAnalysis.aiEstimate / 1000)}k
              </Badge>
            )}
          </div>

          {/* Price Slider */}
          {priceAnalysis && (
            <div className="mb-3">
              {/* Labels */}
              <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                <span>{t.propertyCard.goodDeal}</span>
                <span>{t.propertyCard.overpriced}</span>
              </div>
              
              {/* Slider Track */}
              <div className="relative h-1.5 rounded-full bg-gradient-to-r from-green-500 via-yellow-400 to-red-500">
                {/* Slider Indicator - Triangle Arrow */}
                <div 
                  className="absolute -bottom-0.5 transform -translate-x-1/2"
                  style={{ left: `${priceAnalysis.sliderPosition}%` }}
                >
                  <div 
                    className="w-0 h-0 border-l-[8px] border-r-[8px] border-b-[10px] border-l-transparent border-r-transparent border-b-foreground"
                  />
                </div>
              </div>

              {/* Price Label */}
              <p className={`text-base font-semibold mt-3 text-center ${priceAnalysis.labelColor}`}>
                {priceAnalysis.label}
              </p>
            </div>
          )}

          {/* Location and Details */}
          <div className="flex items-center gap-2 text-muted-foreground text-sm mt-auto">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {property.city}
              {property.district && `, ${property.district}`}
            </span>
            {(property.bedrooms || (property.type === "Апартаменти" && property.bedrooms === null)) && (
              <>
                <span>•</span>
                <span className="whitespace-nowrap flex items-center gap-1">
                  <BedDouble className="h-3.5 w-3.5" />
                  {property.bedrooms === null ? "Многостаен" : property.bedrooms}
                </span>
              </>
            )}
            {property.area && (
              <>
                <span>•</span>
                <span className="whitespace-nowrap">{property.area}m²</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <AuthRequiredDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
    </>
  );
};

export default PropertyCard;
